/**
 * Remotion's `spring()` for the cases the reel uses (from 0 to 1, no delay, no fixed duration), computed the way Remotion computes
 * it: a damped oscillator advanced frame by frame from rest, so the caption pops and the card entrances land on the same values in
 * every renderer. Remotion's own `spring` still drives the React components; this is the same curve for everything else.
 */
export interface SpringConfig { damping?: number; mass?: number; stiffness?: number; overshootClamping?: boolean }
const DEFAULTS = { damping: 10, mass: 1, stiffness: 100, overshootClamping: false };

interface Animation { lastTimestamp: number; current: number; toValue: number; velocity: number }

function advance(animation: Animation, now: number, c: number, m: number, k: number): Animation {
  const { toValue, lastTimestamp, current, velocity } = animation;
  const deltaTime = Math.min(now - lastTimestamp, 64);
  if (c <= 0) throw new Error("Spring damping must be greater than 0");
  const v0 = -velocity;
  const x0 = toValue - current;
  const zeta = c / (2 * Math.sqrt(k * m));
  const omega0 = Math.sqrt(k / m);
  const omega1 = omega0 * Math.sqrt(1 - zeta ** 2);
  const t = deltaTime / 1000;
  const sin1 = Math.sin(omega1 * t);
  const cos1 = Math.cos(omega1 * t);
  const underEnvelope = Math.exp(-zeta * omega0 * t);
  const underFrag = underEnvelope * (sin1 * ((v0 + zeta * omega0 * x0) / omega1) + x0 * cos1);
  const underPosition = toValue - underFrag;
  const underVelocity = zeta * omega0 * underFrag - underEnvelope * (cos1 * (v0 + zeta * omega0 * x0) - omega1 * x0 * sin1);
  const critEnvelope = Math.exp(-omega0 * t);
  const critPosition = toValue - critEnvelope * (x0 + (v0 + omega0 * x0) * t);
  const critVelocity = critEnvelope * (v0 * (t * omega0 - 1) + t * x0 * omega0 * omega0);
  return { toValue, lastTimestamp: now, current: zeta < 1 ? underPosition : critPosition, velocity: zeta < 1 ? underVelocity : critVelocity };
}

const cache = new Map<string, number>();

/** The spring's position (0..1, overshooting unless clamped) `frame` frames after it was released. */
export function spring({ frame, fps, config = {} }: { frame: number; fps: number; config?: SpringConfig }): number {
  const c = config.damping ?? DEFAULTS.damping, m = config.mass ?? DEFAULTS.mass, k = config.stiffness ?? DEFAULTS.stiffness;
  const clampOvershoot = config.overshootClamping ?? DEFAULTS.overshootClamping;
  const key = `${frame}|${fps}|${c}|${m}|${k}|${clampOvershoot}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  let animation: Animation = { lastTimestamp: 0, current: 0, toValue: 1, velocity: 0 };
  const frameClamped = Math.max(0, frame);
  const unevenRest = frameClamped % 1;
  for (let f = 0; f <= Math.floor(frameClamped); f++) {
    if (f === Math.floor(frameClamped)) f += unevenRest;
    animation = advance(animation, (f / fps) * 1000, c, m, k);
  }
  const value = clampOvershoot ? Math.min(animation.current, 1) : animation.current;
  if (cache.size > 20000) cache.clear();
  cache.set(key, value);
  return value;
}
